import type { Confidence, DataSource } from "../../shared/types";

const SOURCE_LABEL: Record<DataSource, { short: string; long: string }> = {
  norskTipping: { short: "NT", long: "Norsk Tipping odds" },
  consensus: {
    short: "Sharp",
    long: "Sharp-book consensus (Pinnacle / Betfair Exchange / Smarkets / Matchbook)",
  },
  model: { short: "Model", long: "Independent Dixon-Coles model (xG + Elo + form)" },
  weather: { short: "Weather", long: "OpenWeather forecast at kickoff" },
};

const ALL_SOURCES: DataSource[] = ["norskTipping", "consensus", "model", "weather"];

/**
 * Confidence chip: shows which of the four data sources contributed signal
 * to this pick, and the rough share as a "3 / 4" indicator. Hover for the
 * full source list with the missing-sources reason.
 */
export function ConfidenceChip({ confidence }: { confidence: Confidence }) {
  const tone =
    confidence.score >= 0.75
      ? "bg-emerald-100 text-emerald-800"
      : confidence.score >= 0.5
        ? "bg-amber-100 text-amber-800"
        : "bg-rose-100 text-rose-800";

  const used = confidence.sources.map((s) => SOURCE_LABEL[s].short).join(", ");
  const missing =
    confidence.missing.length === 0
      ? "All four data sources contributed."
      : `Missing: ${confidence.missing.map((s) => SOURCE_LABEL[s].long).join("; ")}.`;
  const title = `Data sources used: ${used} (${confidence.sources.length} of 4). ${missing}`;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
      title={title}
    >
      {confidence.sources.length}/4
      <span className="flex items-center gap-0.5" aria-hidden>
        {ALL_SOURCES.map((s) => {
          const on = confidence.sources.includes(s);
          return (
            <span
              key={s}
              className={`h-1.5 w-1.5 rounded-full ${on ? "bg-current opacity-90" : "bg-current opacity-25"}`}
            />
          );
        })}
      </span>
    </span>
  );
}
